import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Github } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGitHubSignIn = async () => {
    setGithubLoading(true);
    try {
      const redirectUri = encodeURIComponent(`${window.location.origin}/auth/github/callback`);
      const { data } = await axios.get(`${API_URL}/auth/github/login/authorize?redirect_uri=${redirectUri}`);
      window.location.href = data.url;
    } catch (err) {
      if (err.response?.status === 501) {
        toast.error('GitHub sign-in is not configured on this server.');
      } else {
        toast.error('Could not connect to GitHub');
      }
      setGithubLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password, keepSignedIn);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative flex">
      {/* Animated Background */}
      <div className="absolute inset-0 animated-gradient opacity-20" />

      {/* Left Side - Form */}
      <div className="relative z-10 w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-white text-sm mb-12 transition-colors duration-200"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Header */}
          <div className="mb-10">
            <h1 className="font-serif text-3xl font-medium mb-3">Create your account</h1>
            <p className="text-muted-foreground">
              Start building your portfolio on REZUM
            </p>
          </div>

          {/* GitHub Sign Up */}
          <Button
            type="button"
            onClick={handleGitHubSignIn}
            disabled={githubLoading}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/5 rounded-sm h-12 font-medium gap-2 mb-8"
          >
            <Github className="w-5 h-5" />
            {githubLoading ? 'Redirecting...' : 'Continue with GitHub'}
          </Button>

          {/* Divider */}
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#050505] px-4 text-xs text-muted-foreground">or create account with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="bg-transparent border-white/20 rounded-sm h-12 focus:border-white placeholder:text-muted-foreground/50"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-transparent border-white/20 rounded-sm h-12 focus:border-white placeholder:text-muted-foreground/50"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="bg-transparent border-white/20 rounded-sm h-12 focus:border-white placeholder:text-muted-foreground/50 pr-12"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Keep me signed in */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                id="keep-signed-in-register"
                onClick={() => setKeepSignedIn(!keepSignedIn)}
                className={`w-5 h-5 border rounded-sm flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                  keepSignedIn
                    ? 'bg-white border-white'
                    : 'bg-transparent border-white/30 hover:border-white/60'
                }`}
                data-testid="keep-signed-in-checkbox"
                aria-checked={keepSignedIn}
                role="checkbox"
              >
                {keepSignedIn && (
                  <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                  </svg>
                )}
              </button>
              <label
                htmlFor="keep-signed-in-register"
                onClick={() => setKeepSignedIn(!keepSignedIn)}
                className="text-sm text-muted-foreground cursor-pointer select-none hover:text-white transition-colors"
              >
                Keep me signed in
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-12 font-medium"
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          {/* Login Link */}
          <p className="mt-8 text-center text-muted-foreground text-sm">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-white hover:underline"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex w-1/2 items-center justify-center border-l border-white/10">
        <div className="text-center px-12">
          <div className="font-mono text-6xl text-muted-foreground/20 mb-6">{'[ ]'}</div>
          <p className="font-serif text-2xl text-muted-foreground">
            Your projects, organized
          </p>
        </div>
      </div>
    </div>
  );
}
